import { CustomButton } from "@/components/custom/custom-button";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { FormUserSelectInput } from "./form-user-select-input";
import { showToast } from "@/redux/actions/toast-actions";
import { addRefereeSchema } from "@/schemas/referee.schema";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  styled,
  Stack,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 500,
    width: "100%",
  },
}));

const RefereeDialog = ({
  open,
  onClose,
  mutationFn,
  referee,
  mode = "addReferee",
}) => {
  const isEditing = Boolean(referee);
  const isUpdateReferralMode = mode === "updateReferral";

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { userId } = useParams();

  const formik = useFormik({
    initialValues: isUpdateReferralMode
      ? {
          refereeUserId: Number(userId),
          referralUserId: referee?.referralUserId ?? "",
        }
      : {
          referralUserId: Number(userId),
          refereeUserId: referee?.refereeUserId ?? "",
        },
    enableReinitialize: true,
    validationSchema: addRefereeSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      const payload = isEditing
        ? {
            referralUserId: Number(values.referralUserId),
            refereeUserId: Number(values.refereeUserId),
          }
        : {
            referralUserId: Number(values.referralUserId),
            refereeUserId: Number(values.refereeUserId),
            hasReferral: true,
          };

      mutation.mutate(payload);
    },
  });

  const mutation = useMutation({
    mutationFn: (payload) => mutationFn(payload),
    onSuccess: (data) => {
      formik.resetForm();
      dispatch(
        showToast(
          data?.message ??
            (isUpdateReferralMode
              ? "Referral updated"
              : isEditing
                ? "Referee updated"
                : "Referee added"),
          "success",
        ),
      );
      queryClient.invalidateQueries({ queryKey: ["referees", userId] });
      onClose();
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Something went wrong", "error"));
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    mutation.reset();
    onClose();
  };

  const title = isUpdateReferralMode
    ? "Update Referral"
    : isEditing
      ? "Update Referee"
      : "Add Referee";

  const subtitle = isUpdateReferralMode
    ? "Search and select the user who referred this account."
    : isEditing
      ? "Edit the referral record below."
      : "Select the user to attach as a referee to this account.";

  const submitLabel = isUpdateReferralMode
    ? mutation.isPending
      ? "Updating..."
      : "Update Referral"
    : isEditing
      ? mutation.isPending
        ? "Updating..."
        : "Update Referee"
      : mutation.isPending
        ? "Adding..."
        : "Add Referee";

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 2,
          pt: 2,
          pb: 0,
        }}
      >
        <Box>
          <DialogTitle
            sx={{
              p: 0,
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {title}
          </DialogTitle>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {subtitle}
          </Typography>
        </Box>

        <Stack spacing={2}>
          {isUpdateReferralMode ? (
            <>
              <FormTextInput
                formik={formik}
                name="refereeUserId"
                label="Referee User ID (current user)"
                type="number"
                isDisabled
              />
              <FormUserSelectInput
                formik={formik}
                name="referralUserId"
                label="Referral (search by name or email)"
              />
            </>
          ) : (
            <>
              <FormTextInput
                formik={formik}
                name="referralUserId"
                label="Referral User ID (current user)"
                type="number"
                isDisabled
              />
              <FormUserSelectInput
                formik={formik}
                name="refereeUserId"
                label="Referee (search by name or email)"
              />
            </>
          )}
        </Stack>

        {mutation.isError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {mutation.error?.data?.[0] ?? "Failed. Try again."}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 2, pb: 1, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
          sx={{ px: 2 }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || !formik.dirty || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {submitLabel}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default RefereeDialog;
